// =============================================================================
// Panel warstw — lista węzłów w KOLEJNOŚCI RYSOWANIA.
//
// Kolejność nie jest kosmetyczna: enkodery rysują węzły po kolei, więc ostatni
// przykrywa wcześniejsze. Dlatego lista idzie z góry na dół tak, jak leci
// rysowanie, a strzałki przesuwają węzeł w tej samej tablicy, którą zapisujemy.
// Odwrócenie listy „żeby wierzch był na górze" wyglądałoby znajomo z edytorów
// graficznych i kłamałoby o modelu.
//
// Węzły zagnieżdżone (wewnątrz `repeat`) NIE są tu wypisane. Są częścią
// powtórzenia, a ich kolejność jest kolejnością w wierszu, nie na etykiecie —
// mieszanie obu w jednej liście dawałoby strzałki, które raz robią jedno, raz
// drugie.
//
// NAZWA WARSTWY MA BYĆ CZYTELNA I TO JEST WYMÓG, NIE OZDOBA. Do 2026-08-26
// wiersz był jednolinijkowy: nazwa `flex-1 truncate`, a za nią CZTERY przyciski
// `Button size="sm"` — każdy z wysokością kontrolki i 12 px marginesu po obu
// stronach. W kolumnie 220 px zostawało na nazwę tyle, że lista czytała się
// „R..", „T..", „P..", „L..", „T..", „T..", „T..", „T..", „K.." — dziewięć
// warstw nie do odróżnienia, w panelu, którego jedynym zadaniem jest
// odróżnianie warstw. Dlatego dziś: nazwa w pierwszej linii, rodzaj w drugiej,
// a przełączniki to gołe ikony 24×24 px, nie przyciski z paddingiem kontrolki.
// =============================================================================

import type { LayoutNode, PrintLayout } from '@insolitum/print-templates';
import { cn } from '@insolitum/ui';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  type LucideIcon,
} from 'lucide-react';

const NAZWY: Record<LayoutNode['kind'], string> = {
  text: 'Tekst',
  barcode: 'Kod kreskowy',
  qr: 'Kod QR',
  line: 'Linia',
  box: 'Ramka',
  image: 'Obraz',
  repeat: 'Powtórzenie',
};

/**
 * Ikona-przełącznik w wierszu warstwy.
 *
 * Świadomie NIE jest to `<Button>` z biblioteki: `.btn` ma wysokość kontrolki
 * (36 px) i 12 px paddingu, więc cztery takie przyciski zjadały 144 px z 220 px
 * kolumny — czyli całą nazwę warstwy. Tu liczy się powierzchnia kliknięcia
 * 24×24 px i nic poza nią.
 */
function Przelacznik({
  ikona: Ikona,
  tytul,
  wlaczony,
  wylaczony,
  onClick,
}: {
  ikona: LucideIcon;
  tytul: string;
  wlaczony?: boolean;
  wylaczony?: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      title={tytul}
      aria-label={tytul}
      disabled={wylaczony}
      onClick={onClick}
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors',
        'text-aurora-subtle hover:bg-aurora-bg-subtle hover:text-aurora-text',
        'disabled:pointer-events-none disabled:opacity-30',
        wlaczony && 'text-aurora-brand'
      )}
    >
      <Ikona className="h-3.5 w-3.5" />
    </button>
  );
}

export function PanelWarstw({
  layout,
  zaznaczony,
  pozaObszarem,
  onZaznacz,
  onZmienKolejnosc,
  onPrzelacz,
}: {
  layout: PrintLayout;
  zaznaczony: string | null;
  /**
   * Identyfikatory węzłów wyjeżdżających poza obszar zadruku — z serwera.
   * Bramka PUBLIKACJI, nie zapisu, więc wiersz dostaje ostrzeżenie, a nie
   * blokadę: układ zapisuje się dalej, tylko nie da się go opublikować.
   */
  pozaObszarem: ReadonlySet<string>;
  onZaznacz: (id: string) => void;
  onZmienKolejnosc: (id: string, kierunek: -1 | 1) => void;
  onPrzelacz: (id: string, pole: 'ukryty' | 'zablokowany') => void;
}) {
  if (layout.nodes.length === 0) {
    return (
      <p className="p-4 text-xs text-aurora-subtle">
        Układ jest pusty — dodaj element paskiem u góry.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-aurora-border">
      {layout.nodes.map((node, i) => {
        const wystaje = pozaObszarem.has(node.id);
        return (
          <li
            key={node.id}
            className={cn(
              'flex cursor-pointer items-center gap-1.5 border-l-2 px-2 py-1.5 transition-colors',
              zaznaczony === node.id
                ? 'border-l-aurora-brand bg-aurora-brand/10'
                : 'border-l-transparent hover:bg-aurora-bg-subtle'
            )}
            onClick={() => onZaznacz(node.id)}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                {wystaje && (
                  <AlertTriangle
                    className="h-3 w-3 shrink-0 text-amber-400"
                    aria-label="Wyjeżdża poza obszar zadruku"
                  />
                )}
                <span
                  className={cn(
                    'truncate text-xs',
                    node.ukryty ? 'text-aurora-subtle line-through' : 'text-aurora-text'
                  )}
                  title={node.nazwa || NAZWY[node.kind]}
                >
                  {node.nazwa || NAZWY[node.kind]}
                </span>
              </div>
              <span className="block truncate text-[10px] leading-tight text-aurora-subtle">
                {node.nazwa ? NAZWY[node.kind] : `${i + 1}. w kolejności rysowania`}
              </span>
            </div>

            <Przelacznik
              ikona={node.ukryty ? EyeOff : Eye}
              tytul={node.ukryty ? 'Pokaż' : 'Ukryj'}
              wlaczony={node.ukryty}
              onClick={(e) => {
                e.stopPropagation();
                onPrzelacz(node.id, 'ukryty');
              }}
            />
            <Przelacznik
              ikona={node.zablokowany ? Lock : Unlock}
              tytul={node.zablokowany ? 'Odblokuj' : 'Zablokuj'}
              wlaczony={node.zablokowany}
              onClick={(e) => {
                e.stopPropagation();
                onPrzelacz(node.id, 'zablokowany');
              }}
            />
            <Przelacznik
              ikona={ChevronUp}
              tytul="Wyżej w kolejności rysowania"
              wylaczony={i === 0}
              onClick={(e) => {
                e.stopPropagation();
                onZmienKolejnosc(node.id, -1);
              }}
            />
            <Przelacznik
              ikona={ChevronDown}
              tytul="Niżej w kolejności rysowania"
              wylaczony={i === layout.nodes.length - 1}
              onClick={(e) => {
                e.stopPropagation();
                onZmienKolejnosc(node.id, 1);
              }}
            />
          </li>
        );
      })}
    </ul>
  );
}
